import { atom } from '@reatom/framework'
import { useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { SegmentControl } from '@blade/ui-kit'
import { selectedNetworkAtom } from '@root/entities/network'
import { StatisticsPriceTypeTabs } from '@root/features/profile/board-statistics/ui/common/statistics-price-type-tabs'
import {
  reatomWalletsTradingAnalytics,
  useOwnProfileWallets,
} from '@root/features/services/wallet-profile'
import { reatomUserTradingAnalytics } from '@root/features/user/user-trading-analytics/atoms'
import {
  USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP,
  USER_TRADING_ANALYTICS_PERIODS,
} from '@root/features/user/user-trading-analytics/models'
import {
  AnalysisBlock,
  AvgBuyMcDistributionBlock,
  DistributionBlock,
  PhishingCheckBlock,
  UserProfilePnl,
  WalletSelectorBlock,
} from '@root/features/user/user-trading-analytics/ui'
import { useTranslation } from '@root/shared/localization'

import type { ProfileOwner } from '../../../features/user/profile/model/profile-owner'
import type { DelugeId } from '@root/entities/user-short'
import type { UserTradingAnalyticsPeriod } from '@root/features/user/user-trading-analytics/models'

type Props = {
  delugeId: DelugeId
  owner: ProfileOwner
}

export const ProfileBoardStatistics: React.FC<Props> = ({
  delugeId,
  owner,
}) => {
  const { t } = useTranslation()

  // The account's own wallets, because the list that used to arrive with the
  // profile came from a route that was never ported and was always empty.
  const ownWallets = useOwnProfileWallets()
  const [network] = useAtom(selectedNetworkAtom)

  const model = useMemo(
    () =>
      reatomUserTradingAnalytics(
        {
          delugeId,
          wallets: ownWallets,
          // Built from whatever the selector has resolved — one wallet or all
          // of them — so the figures are a real read rather than an error.
          source: (selected) =>
            // eslint-disable-next-line @reatom/unit-naming-rule
            reatomWalletsTradingAnalytics({
              network,
              walletsAtom: atom(
                (ctx) => ctx.spy(selected).map((wallet) => wallet.address),
                'walletsAtom',
              ),
            }),
        },
        'model',
      ),
    [delugeId, ownWallets, network],
  )

  const [period, setPeriod] = useAtom(model.periodAtom)
  const [priceType, setPriceType] = useAtom(model.priceTypeAtom)

  return (
    <Root>
      <Dashboard>
        <HeaderLeft>
          <SegmentControlStyled value={period} onChange={setPeriod}>
            {USER_TRADING_ANALYTICS_PERIODS.map((period) => (
              <SegmentControl.Item key={period} value={period}>
                {t(
                  USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP[period],
                ).toUpperCase()}
              </SegmentControl.Item>
            ))}
          </SegmentControlStyled>

          <StatisticsPriceTypeTabs
            priceType={priceType}
            onPriceTypeChange={setPriceType}
          />
        </HeaderLeft>

        <HeaderRight>
          <WalletSelectorBlock model={model} />
        </HeaderRight>
        <Pnl model={model} owner={owner} />
        <Analysis model={model} />
        <Distribution>
          <DistributionBlock model={model} />
        </Distribution>
        <AvgBuyMcDistribution model={model} />
        <PhishingCheck model={model} />
      </Dashboard>
    </Root>
  )
}

const Root = styled.div`
  height: 100%;
`
const HeaderLeft = styled.div`
  grid-area: header-left;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: space-between;
`
const HeaderRight = styled.div`
  grid-area: header-right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`
const Dashboard = styled.div`
  padding: 9px 12px;
  display: grid;
  background: transparent;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto 320px 320px;
  grid-template-areas:
    'header-left header-left header-right'
    'chart chart analysis'
    'distribution avg-buy-mc-distribution phishing-check';

  gap: 10px;

  position: relative;
  overflow: auto;
`
const Pnl = styled(UserProfilePnl)`
  grid-area: chart;
`
const Distribution = styled.div`
  grid-area: distribution;
  display: grid;
  height: 100%;

  cursor: pointer;
`
const AvgBuyMcDistribution = styled(AvgBuyMcDistributionBlock)`
  grid-area: avg-buy-mc-distribution;
`
const Analysis = styled(AnalysisBlock)`
  grid-area: analysis;
`
const PhishingCheck = styled(PhishingCheckBlock)`
  grid-area: phishing-check;
`

const SegmentControlStyled = styled(SegmentControl<UserTradingAnalyticsPeriod>)`
  background: var(--background-1);
`
